import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Optional label for which area failed (shown in the fallback). */
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render/lifecycle errors in its subtree so one component's failure
 * shows a localized message instead of white-screening the whole app. Added
 * after a Google Maps loader bug blanked the entire board (BOARD-MAP-01). Wrap
 * the routed content (and reset on navigation) so the shell always survives.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface for debugging; a real logger/telemetry sink lands later.
    console.error("ErrorBoundary caught:", error, info);
  }

  handleReset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full items-center justify-center p-10">
          <div className="max-w-md rounded-xl border border-red-200 bg-red-50 p-6 text-center">
            <h2 className="text-sm font-semibold text-red-900">
              Something went wrong
              {this.props.label ? ` in ${this.props.label}` : ""}.
            </h2>
            <p className="mt-1 text-xs text-red-700">
              {this.state.error.message || "An unexpected error occurred."}
            </p>
            <button
              type="button"
              onClick={this.handleReset}
              className="mt-4 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
